import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";

/** Fade-and-rise on first entry into the viewport. Triggers once, never on scroll-back. */
export function Reveal({ children, delay = 0, className = "", as = "div", testId }) {
  const still = useReducedMotion();
  const Tag = motion[as] || motion.div;
  if (still) {
    return (
      <div className={className} data-testid={testId}>
        {children}
      </div>
    );
  }
  return (
    <Tag
      className={className}
      data-testid={testId}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.44, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </Tag>
  );
}

export function OrangeButton({ to, href, children, className = "", testId, onClick }) {
  const base =
    "group inline-flex items-center gap-2 rounded-full bg-[#F2811D] px-6 py-3 text-[15px] font-bold text-white transition-colors duration-200 hover:bg-[#D96C0C] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F2811D]";
  const content = (
    <>
      {children}
      <ArrowRight className="h-4 w-4 nudge" />
    </>
  );
  if (to) {
    return (
      <Link to={to} className={`${base} ${className}`} data-testid={testId} onClick={onClick}>
        {content}
      </Link>
    );
  }
  return (
    <a href={href} className={`${base} ${className}`} data-testid={testId} onClick={onClick}>
      {content}
    </a>
  );
}

export function GhostButton({ to, href, children, className = "", testId, dark = false }) {
  const base = dark
    ? "group inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3 text-[15px] font-bold text-white transition-colors duration-200 hover:border-white hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FFB25C]"
    : "group inline-flex items-center gap-2 rounded-full border border-[#D9D3C7] px-6 py-3 text-[15px] font-bold text-[#111111] transition-colors duration-200 hover:border-[#111111] hover:bg-[#F2EEE6] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#111111]";
  const content = (
    <>
      {children}
      <ArrowRight className="h-4 w-4 nudge" />
    </>
  );
  if (to) {
    return (
      <Link to={to} className={`${base} ${className}`} data-testid={testId}>
        {content}
      </Link>
    );
  }
  return (
    <a href={href} className={`${base} ${className}`} data-testid={testId}>
      {content}
    </a>
  );
}

export function Eyebrow({ children, dark = false, className = "" }) {
  return (
    <p
      className={`text-xs font-bold uppercase tracking-[0.22em] ${
        dark ? "text-[#FFB25C]" : "text-[#F2811D]"
      } ${className}`}
    >
      {children}
    </p>
  );
}

export function Section({ children, className = "", tone = "white", testId }) {
  const bg = tone === "cream" ? "bg-[#F7F4EF]" : "bg-white";
  return (
    <section className={`${bg} px-5 py-16 sm:px-8 sm:py-20 lg:py-24 ${className}`} data-testid={testId}>
      <Reveal className="mx-auto max-w-[1180px]">{children}</Reveal>
    </section>
  );
}

/** Full-bleed dark band with the playbook's charcoal + orange gradient treatment. */
export function DarkSection({ children, className = "", testId, tight = false, raw = false }) {
  return (
    <section
      className={`dark-band dot-pattern px-5 sm:px-8 ${tight ? "py-14 sm:py-16" : "py-16 sm:py-20 lg:py-24"} ${className}`}
      data-testid={testId}
    >
      {raw ? (
        <div className="mx-auto max-w-[1180px]">{children}</div>
      ) : (
        <Reveal className="mx-auto max-w-[1180px]">{children}</Reveal>
      )}
    </section>
  );
}

/** Bordered box, matching the playbook's white-hairline cards on dark. */
export function BorderBox({ children, className = "", dark = false, testId }) {
  return (
    <div
      className={`card-lift rounded-2xl border p-6 sm:p-7 ${
        dark ? "border-white/15 bg-white/[0.04] backdrop-blur-sm" : "border-[#E6E1D8] bg-white"
      } ${className}`}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

export function Stat({ value, label, dark = false, testId }) {
  return (
    <div data-testid={testId}>
      <p
        className={`font-heading text-3xl font-extrabold tracking-tight sm:text-4xl ${
          dark ? "text-[#FFB25C]" : "text-[#F2811D]"
        }`}
      >
        {value}
      </p>
      <p
        className={`mt-2 text-sm leading-relaxed ${dark ? "text-white/70" : "text-[#55504A]"}`}
      >
        {label}
      </p>
    </div>
  );
}

export function DotList({ items, dark = false, className = "", testId }) {
  return (
    <ul className={`space-y-3 ${className}`} data-testid={testId}>
      {items.map((item, i) => (
        <li key={i} className="flex gap-3">
          <span
            className={`mt-[9px] h-1.5 w-1.5 flex-shrink-0 rounded-full ${
              dark ? "bg-[#FFB25C]" : "bg-[#F2811D]"
            }`}
          />
          <span className={`text-[15px] leading-relaxed ${dark ? "text-white/80" : "body-text"}`}>
            {typeof item === "string" ? item : (
              <>
                <strong className={dark ? "text-white" : "text-[#111111]"}>{item.title}</strong>{" "}
                {item.body}
              </>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function PlaceholderBadge({ className = "" }) {
  return (
    <span
      className={`inline-flex items-center rounded-full bg-black/70 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white backdrop-blur-sm ${className}`}
      title="Neutral stock photography standing in for a real campaign asset"
    >
      Placeholder image
    </span>
  );
}

/** Image inside a fixed frame that scales gently on hover. */
export function ImageFrame({ src, alt, className = "", ratio = "aspect-[4/3]", children, testId }) {
  return (
    <div className={`img-frame overflow-hidden rounded-2xl bg-[#F0ECE4] ${className}`} data-testid={testId}>
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className={`${ratio} w-full object-cover`}
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
      {children}
    </div>
  );
}
